import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import {
  map,
  distinctUntilChanged,
  shareReplay,
  takeUntil
} from 'rxjs/operators';
import { BlocklyConfigState, DownloadOption } from '../domain/blockly/blockly.types';
import { DownloadOptionsService } from './download-options.service';
import { getCodeLangConfig, isSupportedCodeLang } from '../domain/code-gen/code-lang.utils';
import { DEFAULT_CODE_LANG } from '../domain/code-gen/code-lang.config';
import { getToolbox } from 'src/app/shared/utils/toolbox';
import { SupportedCodeLang } from '../domain/code-gen/code-lang.types';

@Injectable({ providedIn: 'root' })
export class BlocklyConfigService implements OnDestroy {

  private readonly destroy$ = new Subject<void>();

  private readonly configState$ = new BehaviorSubject<BlocklyConfigState>({
    codeLang: DEFAULT_CODE_LANG,
    moduleKey: '',
    hideGatewayOption: false,
    isAdmin: false,
    isDirect: false,
  });

  private readonly toolboxState$ = new Subject<string>();

  public readonly config$ = this.configState$.asObservable();

  public readonly toolbox$: Observable<string> = this.toolboxState$.asObservable();

  public readonly downloadOptions$: Observable<DownloadOption[]> = this.config$.pipe(
    map(({ codeLang, moduleKey, hideGatewayOption }) =>
      this.downloadService.computeOptions(codeLang, moduleKey, hideGatewayOption)
    ),
    distinctUntilChanged(this.downloadService.compareOptions.bind(this.downloadService)),
    shareReplay(1)
  );

  constructor(private readonly downloadService: DownloadOptionsService) {
    this.setupToolboxLoader();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.configState$.complete();
    this.toolboxState$.complete();
  }

  private setupToolboxLoader(): void {
    this.config$.pipe(
      takeUntil(this.destroy$),
      map(config => ({ codeLang: config.codeLang, moduleKey: config.moduleKey }))
    ).subscribe(({ codeLang, moduleKey }) => {
      this.loadToolbox(codeLang, moduleKey);
    });
  }

  private async loadToolbox(codeLang: string, moduleKey: string): Promise<void> {
    if (!codeLang) return;

    try {
      const toolboxXml = await getToolbox(codeLang as SupportedCodeLang, moduleKey || undefined);

      if (toolboxXml?.trim()) {
        this.toolboxState$.next(toolboxXml);
      }
    } catch (error) {
    }
  }

  get current(): Readonly<BlocklyConfigState> {
    return this.configState$.value;
  }

  updateConfig(partial: Partial<BlocklyConfigState>): void {
    if (partial.codeLang && !isSupportedCodeLang(partial.codeLang)) {
      return;
    }

    const newConfig = { ...this.current, ...partial };
    this.configState$.next(newConfig);
  }

  getFileExtension(): string {
    return getCodeLangConfig(this.current.codeLang)?.fileExtension || '';
  }

  shouldMarkCodePresent(code: string): boolean {
    const { isAdmin, isDirect } = this.current;
    return isAdmin || isDirect || code.trim() !== '';
  }
}
